import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Card, DatePicker, Dropdown, Empty, Modal, Popconfirm, Space, Table, Tag, Tooltip, message } from "antd";
import { BarcodeOutlined, CloseCircleOutlined, EyeOutlined, IdcardOutlined, PrinterOutlined, ReloadOutlined, SyncOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import dayjs from "dayjs";
import locale from "antd/es/date-picker/locale/nl_NL";
import urls from "urls";
import Loading from "components/shared-components/Loading";
import { DATE_FORMAT_DD_MM_YYYY } from "constants/DateConstant";
import { WAVE_STATUS } from "constants/PickingConstant";
import { usePickers, usePickResults, usePickRoute, usePickWaves, useProducts } from "data/hooks";
import { buildProductIndex } from "functions/pickLines";
import { buildPickRouteIndex } from "functions/pickRoute";
import { summariseWaveBoxes } from "functions/waveDocument";
import PackingSlip from "components/shared-components/Table/PackingSlip";
import CommandCardSheet from "components/shared-components/Print/CommandCardSheet";
import PickDayStepsBar from "components/shared-components/PickDay/PickDayStepsBar";
import { A4_PAGE_STYLE, toDayjs, todayPickDay } from "../shared";
import WaveDetailsDrawer from "./WaveDetailsDrawer";

const STATUS_COLORS = {
  [WAVE_STATUS.OPEN]: "default",
  [WAVE_STATUS.BUSY]: "processing",
  [WAVE_STATUS.DONE]: "success",
  [WAVE_STATUS.CANCELLED]: "error",
};

/**
 * Orderpicking -> Pickrondes: the rounds of one pick day, as the scanners see them.
 *
 * Rounds are composed on the order overview, where staff already select orders; this page is
 * where a round is looked at, printed (stap 4), processed back onto the orders (stap 5) or
 * cancelled while nobody has started it. The pick day and an open round live in the URL
 * (`?pickDay=…&wave=…`), so the compose screen can link straight to the round it just made.
 */
const PickWaves = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const pickDay = params.get("pickDay") || todayPickDay();
  const openWaveId = params.get("wave");

  const { waves, isLoading, error, refresh, cancelWave, fetchWaveOrders, markPrinted } = usePickWaves(pickDay);
  const { products } = useProducts();
  const { entries: pickRouteEntries } = usePickRoute();
  const { projections, projectWave } = usePickResults(waves);
  const { nameOf } = usePickers();
  const [projectionReport, setProjectionReport] = useState(null);
  const productIndex = useMemo(() => buildProductIndex(products), [products]);
  const pickRouteIndex = useMemo(() => buildPickRouteIndex(pickRouteEntries), [pickRouteEntries]);
  const [busyId, setBusyId] = useState(null);

  // Printing: the slips of one round, or the command card. Rendered only while printing; the
  // barcodes are drawn synchronously, so everything is there when react-to-print copies it.
  const slipRef = useRef(null);
  const cardRef = useRef(null);
  const [slipJob, setSlipJob] = useState(null);
  const [cardJob, setCardJob] = useState(false);
  const printSlips = useReactToPrint({
    contentRef: slipRef,
    documentTitle: slipJob ? `Pakbonnen ronde ${slipJob.wave.waveNumber} ${slipJob.wave.pickDay}${slipJob.forPicking ? " zonder scanner" : ""}` : "Pakbonnen",
    pageStyle: A4_PAGE_STYLE,
    onAfterPrint: () => {
      if (slipJob) markPrinted(slipJob.wave).catch(() => null);
      setSlipJob(null);
    },
  });
  const printCard = useReactToPrint({
    contentRef: cardRef,
    documentTitle: "Commandokaart",
    pageStyle: A4_PAGE_STYLE,
    onAfterPrint: () => setCardJob(false),
  });
  useEffect(() => {
    if (slipJob) printSlips();
  }, [slipJob, printSlips]);
  useEffect(() => {
    if (cardJob) printCard();
  }, [cardJob, printCard]);

  /**
   * Process a round's events onto its orders. Runs by itself whenever the page sees events the
   * wave has not been processed up to (the fold is idempotent, so running it twice is harmless),
   * and by hand from the button, which also shows the per-order result.
   */
  const inFlight = useRef(new Set());
  const runProjection = async (wave, { report = false } = {}) => {
    const id = wave.waveId || wave.id;
    if (inFlight.current.has(id)) return;
    inFlight.current.add(id);
    try {
      const result = await projectWave(wave);
      if (report) setProjectionReport({ wave, ...result });
      else if (!result.ok) message.warning(t("orderpicking.waves.projection_partial", { number: wave.waveNumber }), 6);
    } catch (projectionError) {
      message.error(projectionError.message, 6);
    } finally {
      inFlight.current.delete(id);
    }
  };

  useEffect(() => {
    waves.forEach((wave) => {
      const projection = projections[wave.waveId || wave.id];
      if (!projection || projection.eventCount === 0 || wave.status === WAVE_STATUS.CANCELLED) return;
      const stored = wave.projection;
      if (!!stored && stored.lastEventId === projection.lastEventId && stored.eventCount === projection.eventCount) return;
      runProjection(wave);
    });
    // runProjection is recreated every render; the effect only has to react to new events
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waves, projections]);

  /** The slips of a round; `forPicking` is the picker's version, for when the scanners are down. */
  const handlePrintSlips = async (wave, { forPicking = false } = {}) => {
    setBusyId(wave.id);
    try {
      const orders = await fetchWaveOrders(wave);
      if (orders.length !== (wave.orders || []).length) {
        message.warning(t("orderpicking.waves.print_missing_orders", { count: (wave.orders || []).length - orders.length }), 6);
      }
      const bySequence = new Map((wave.orders || []).map((waveOrder) => [waveOrder.orderRef, waveOrder]));
      const sorted = [...orders].sort(
        (a, b) => bySequence.get(`${a.shopSource}/${a.id}`).sequence - bySequence.get(`${b.shopSource}/${b.id}`).sequence,
      );
      setSlipJob({ wave, orders: sorted, waveOrdersByRef: Object.fromEntries(bySequence), forPicking });
    } catch (printError) {
      message.error(printError.message, 6);
    } finally {
      setBusyId(null);
    }
  };

  const setParams = (next) => {
    const merged = new URLSearchParams(location.search);
    Object.entries(next).forEach(([key, value]) => (value ? merged.set(key, value) : merged.delete(key)));
    navigate(`${urls.pickWaves}?${merged.toString()}`, { replace: true });
  };

  const openWave = waves.find((wave) => (wave.waveId || wave.id) === openWaveId) || null;

  const handleCancel = async (wave) => {
    setBusyId(wave.id);
    try {
      const result = await cancelWave(wave);
      if (result.ok) message.success(t("orderpicking.waves.cancelled", { number: wave.waveNumber }), 4);
      else message.error(t("orderpicking.waves.cancel_refused"), 5);
    } catch (cancelError) {
      message.error(cancelError.message, 6);
    } finally {
      setBusyId(null);
    }
  };

  const columns = [
    {
      title: t("orderpicking.waves.round"),
      dataIndex: "waveNumber",
      width: 90,
      render: (number) => <strong>{t("orderpicking.waves.round_number", { number })}</strong>,
    },
    {
      title: t("orderpicking.waves.status"),
      dataIndex: "status",
      width: 150,
      render: (status) => <Tag color={STATUS_COLORS[status] || "default"}>{t(`orderpicking.waves.status_${status}`)}</Tag>,
    },
    { title: t("orderpicking.waves.orders"), dataIndex: "orderCount", width: 100 },
    { title: t("orderpicking.waves.lines"), dataIndex: "lineCount", width: 90 },
    {
      // What the picker takes into the freezer: the round is picked in one go, straight into boxes
      title: t("orderpicking.waves.boxes"),
      dataIndex: "boxes",
      width: 150,
      render: (_, wave) => {
        const boxes = summariseWaveBoxes(wave);
        if (wave.status === WAVE_STATUS.CANCELLED || boxes.small + boxes.large + boxes.unknown === 0) return <span style={{ color: "#bfbfbf" }}>-</span>;
        const text = t("orderpicking.waves.boxes_value", { small: boxes.small, large: boxes.large });
        return boxes.uncertainOrders > 0 ? (
          <Tooltip title={t("orderpicking.waves.boxes_uncertain", { count: boxes.uncertainOrders })}>
            <span>{text} *</span>
          </Tooltip>
        ) : (
          text
        );
      },
    },
    {
      title: t("orderpicking.waves.created"),
      dataIndex: "createdAt",
      render: (createdAt, wave) => (
        <span>
          {dayjs(createdAt).format("D MMM HH:mm")}
          <span style={{ color: "#8c8c8c" }}> · {wave.createdBy || "-"}</span>
        </span>
      ),
    },
    {
      title: t("orderpicking.waves.progress"),
      dataIndex: "progress",
      width: 190,
      render: (_, wave) => {
        const projection = projections[wave.waveId || wave.id];
        if (!projection || projection.eventCount === 0) return <span style={{ color: "#bfbfbf" }}>-</span>;
        const states = Object.values(projection.orders);
        const closed = states.filter((state) => ["picked", "partial", "aborted"].includes(state.status)).length;
        const partial = states.filter((state) => state.status === "partial").length;
        const aborted = states.filter((state) => state.status === "aborted").length;
        return (
          <Space size={4} wrap>
            <span>{t("orderpicking.waves.progress_orders", { closed, count: states.length })}</span>
            {partial > 0 && <Tag color="orange">{t("orderpicking.day.partial", { count: partial })}</Tag>}
            {aborted > 0 && <Tag color="red">{t("orderpicking.day.aborted", { count: aborted })}</Tag>}
          </Space>
        );
      },
    },
    {
      title: t("orderpicking.waves.picker"),
      dataIndex: "pickerId",
      width: 130,
      render: (pickerId) =>
        pickerId ? (
          <Tooltip title={pickerId}>
            <span>{nameOf(pickerId) || pickerId}</span>
          </Tooltip>
        ) : (
          <span style={{ color: "#bfbfbf" }}>-</span>
        ),
    },
    {
      title: "",
      dataIndex: "actions",
      width: 200,
      render: (_, wave) => (
        <Space size={4}>
          <Tooltip title={t("orderpicking.waves.details")}>
            <Button size="small" icon={<EyeOutlined />} onClick={() => setParams({ wave: wave.waveId || wave.id })} />
          </Tooltip>
          {wave.status !== WAVE_STATUS.CANCELLED && (
            <Dropdown.Button
              size="small"
              type={wave.printedAt ? "default" : "primary"}
              loading={busyId === wave.id && !slipJob}
              onClick={() => handlePrintSlips(wave)}
              menu={{
                items: [{ key: "forPicking", icon: <PrinterOutlined />, label: t("orderpicking.waves.print_slips_for_picking") }],
                onClick: () => handlePrintSlips(wave, { forPicking: true }),
              }}
              buttonsRender={([main, more]) => [
                <Tooltip
                  key="main"
                  title={wave.printedAt ? t("orderpicking.waves.printed_at", { when: dayjs(wave.printedAt).format("D MMM HH:mm") }) : t("orderpicking.waves.print_slips")}
                >
                  {main}
                </Tooltip>,
                more,
              ]}
            >
              <PrinterOutlined />
            </Dropdown.Button>
          )}
          {wave.status !== WAVE_STATUS.CANCELLED && !!projections[wave.waveId || wave.id] && projections[wave.waveId || wave.id].eventCount > 0 && (
            <Tooltip title={t("orderpicking.waves.process")}>
              <Button size="small" icon={<SyncOutlined />} onClick={() => runProjection(wave, { report: true })} />
            </Tooltip>
          )}
          {wave.status === WAVE_STATUS.OPEN && (
            <Popconfirm
              title={t("orderpicking.waves.confirm_cancel", { number: wave.waveNumber })}
              okText={t("yes")}
              cancelText={t("no")}
              onConfirm={() => handleCancel(wave)}
            >
              <Tooltip title={t("orderpicking.waves.cancel")}>
                <Button size="small" danger icon={<CloseCircleOutlined />} loading={busyId === wave.id} />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Card
      title={t("orderpicking.waves.title")}
      extra={
        <Space>
          <DatePicker
            value={toDayjs(pickDay)}
            locale={locale}
            format={DATE_FORMAT_DD_MM_YYYY}
            allowClear={false}
            onChange={(date) => setParams({ pickDay: date ? date.format(DATE_FORMAT_DD_MM_YYYY) : null, wave: null })}
          />
          <Button icon={<BarcodeOutlined />} onClick={() => setCardJob(true)}>
            {t("orderpicking.waves.print_command_card")}
          </Button>
          <Button icon={<IdcardOutlined />} onClick={() => navigate(urls.pickers)}>
            {t("orderpicking.pickers.nav")}
          </Button>
          <Button icon={<ReloadOutlined />} onClick={() => refresh()}>
            {t("orderpicking.waves.refresh")}
          </Button>
        </Space>
      }
    >
      <PickDayStepsBar pickDay={pickDay} page="waves" />
      <p style={{ maxWidth: 820, color: "#6b6b6b" }}>{t("orderpicking.waves.explanation")}</p>
      {!!error && <Alert type="error" showIcon className="mb-3" message={t("orderpicking.waves.error")} description={error.message} />}
      {isLoading ? (
        <Loading />
      ) : (
        <div className="table-responsive">
          <Table
            size="small"
            rowKey="id"
            columns={columns}
            dataSource={waves}
            pagination={false}
            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("orderpicking.waves.empty", { day: pickDay })} /> }}
          />
        </div>
      )}
      <WaveDetailsDrawer
        wave={openWave}
        projection={openWave ? projections[openWave.waveId || openWave.id] || null : null}
        onClose={() => setParams({ wave: null })}
      />
      <Modal
        open={!!projectionReport}
        title={projectionReport ? t("orderpicking.waves.process_title", { number: projectionReport.wave.waveNumber }) : ""}
        onCancel={() => setProjectionReport(null)}
        footer={<Button onClick={() => setProjectionReport(null)}>{t("close")}</Button>}
        width={720}
      >
        {!!projectionReport && (
          <>
            {!projectionReport.ok && <Alert type="warning" showIcon className="mb-3" message={t("orderpicking.waves.projection_partial", { number: projectionReport.wave.waveNumber })} />}
            <Table
              size="small"
              rowKey="orderRef"
              pagination={false}
              dataSource={projectionReport.results}
              columns={[
                {
                  title: t("orderpicking.compose.shop"),
                  dataIndex: "orderRef",
                  width: 70,
                  render: (ref) => <Tag color={ref.startsWith("VCE") ? "green" : "purple"}>{ref.split("/")[0]}</Tag>,
                },
                { title: t("orderpicking.compose.order"), dataIndex: "orderId" },
                {
                  title: t("status"),
                  dataIndex: "status",
                  render: (status, row) =>
                    row.ok ? <Tag>{t(`order.picking_${status}`)}</Tag> : <Tag color="red">{t(`orderpicking.waves.result_${row.reason.replace(/-/g, "_")}`)}</Tag>,
                },
                {
                  title: "",
                  dataIndex: "changed",
                  render: (changed, row) => (row.ok ? (changed ? t("orderpicking.waves.result_written") : t("orderpicking.waves.result_unchanged")) : row.message || ""),
                },
              ]}
            />
          </>
        )}
      </Modal>
      {!!slipJob && (
        <div style={{ display: "none" }}>
          <PackingSlip
            ref={slipRef}
            data={slipJob.orders}
            productIndex={productIndex}
            pickRouteIndex={pickRouteIndex}
            waveOrdersByRef={slipJob.waveOrdersByRef}
            wave={slipJob.wave}
            forPicking={slipJob.forPicking}
          />
        </div>
      )}
      {cardJob && (
        <div style={{ display: "none" }}>
          <CommandCardSheet ref={cardRef} />
        </div>
      )}
    </Card>
  );
};

export default PickWaves;
